import { Capacitor } from '@capacitor/core';
import { createPageUrl, type AppPage } from '../utils/appRoutes';
import { getAnalyticsConsent, isConsentPolicyKnown, subscribeAnalyticsConsent } from './analyticsConsent';

// Consent-gated GA4 via gtag.js. Nothing is loaded, stored or sent until the
// user explicitly accepts analytics, auth has resolved, and the session is not an
// admin. Only route templates and allowlisted event fields ever reach Google.

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [gaDisable: `ga-disable-${string}`]: boolean | undefined;
  }
}

type CommonParams = {
  category_id?: string;
  county_code?: string;
  listing_type?: 'sale' | 'wanted';
};

export interface AnalyticsEventParams {
  search_results_viewed: CommonParams & {
    result_count: number;
    zero_results: boolean;
    search_action: 'initial' | 'submit' | 'filter' | 'paginate';
    has_query: boolean;
  };
  listing_view: CommonParams;
  contact_seller: CommonParams & {
    contact_method: 'message' | 'phone_reveal';
    source_surface: 'listing' | 'seller_profile';
  };
  announcement_start: { listing_type: 'sale' | 'wanted' };
  announcement_submitted: CommonParams;
  sign_up: { method: 'email' };
  login: { method: 'password' | 'google' };
}

export const listingTypeOf = (productType?: string): 'sale' | 'wanted' =>
  productType === 'DEMAND' ? 'wanted' : 'sale';

/** Controlled listing metadata only: category id, county name and sale/wanted. */
export const listingAnalyticsParams = (listing: {
  productType?: string;
  category?: { id?: string } | null;
  locationDto?: { county?: { name?: string } | null } | null;
}): CommonParams => ({
  category_id: listing.category?.id,
  county_code: listing.locationDto?.county?.name,
  listing_type: listingTypeOf(listing.productType),
});

const GA_ID_PATTERN = /^G-[A-Z0-9]+$/;
// Token-bearing and admin screens: never reported; a loaded tag only sees this placeholder.
const RESTRICTED_PAGES = new Set<AppPage>(['resetPassword', 'verifyEmail', 'administration']);
const TEMPLATE_PATHS: Partial<Record<AppPage, string>> = {
  productDetail: '/products/detail',
  userDetails: '/users/profile',
  editProduct: '/announcements/edit',
  category: '/search',
  notFound: '/not-found',
};

let authResolved = false;
let adminSession = false;
let consentSubscribed = false;
let tagInitialized = false;
let route: { page: AppPage; key: string } | null = null;
let lastSentKey: string | null = null;
let previousLocation: string | null = null;
let pendingRouteEvent: { key: string; send: () => void } | null = null;

const getMeasurementId = (): string | null => {
  const id = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '').trim();
  return GA_ID_PATTERN.test(id) ? id : null;
};

export const isAnalyticsConfigured = (): boolean =>
  getMeasurementId() !== null && !Capacitor.isNativePlatform();

const isEligible = () =>
  authResolved && !adminSession && isAnalyticsConfigured() && getAnalyticsConsent() === true;

const gtag = (...args: unknown[]) => window.gtag?.(...args);

const surface = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ? 'pwa' : 'web';

const pageLocation = (page: AppPage) => {
  const path = RESTRICTED_PAGES.has(page) ? '/restricted' : TEMPLATE_PATHS[page] ?? createPageUrl(page);
  return `${window.location.origin}${path}`;
};

const externalReferrer = () => {
  try {
    const origin = new URL(document.referrer).origin;
    if (origin !== window.location.origin && /^https?:/.test(origin)) return `${origin}/`;
  } catch {
    // No or invalid referrer.
  }
  return `${window.location.origin}/`;
};

const routeFields = () => {
  const page = route?.page ?? 'home';
  return {
    page_location: pageLocation(page),
    page_title: `roade.ro - ${page}`,
    page_referrer: previousLocation ?? externalReferrer(),
  };
};

const clearGaCookies = (id: string) => {
  const host = window.location.hostname;
  for (const name of ['_ga', `_ga_${id.slice(2)}`]) {
    for (const domain of ['', `; domain=${host}`, `; domain=.${host}`]) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
    }
  }
};

const initTag = (id: string) => {
  tagInitialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js requires the arguments object itself, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('js', new Date());
  const fields = routeFields();
  gtag('set', fields);
  // No page fields here: config values outrank later `set` calls and would pin every event to the first page.
  gtag('config', id, {
    send_page_view: false,
    cookie_domain: window.location.hostname,
    // 13 months instead of GA's 2-year default; the cookie policy states this duration.
    cookie_expires: 60 * 60 * 24 * 395,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
};

const sendCurrentPageView = () => {
  if (!route || route.key === lastSentKey) return;
  const fields = routeFields();
  gtag('set', fields);
  previousLocation = fields.page_location;
  lastSentKey = route.key;
  if (RESTRICTED_PAGES.has(route.page)) return;
  gtag('event', 'page_view', { ...fields, page_type: route.page, surface: surface() });
};

const sync = () => {
  const id = getMeasurementId();
  if (!id) return;
  const eligible = isEligible();

  // Before the policy version loads every choice reads as null; clearing then would reset returning visitors' IDs.
  if (isConsentPolicyKnown() && getAnalyticsConsent() !== true) clearGaCookies(id);
  if (!eligible) {
    lastSentKey = null;
    if (tagInitialized && !window[`ga-disable-${id}`]) {
      window[`ga-disable-${id}`] = true;
      gtag('consent', 'update', { analytics_storage: 'denied' });
    }
    return;
  }

  if (!tagInitialized) {
    window[`ga-disable-${id}`] = false;
    initTag(id);
  } else if (window[`ga-disable-${id}`]) {
    window[`ga-disable-${id}`] = false;
    gtag('consent', 'update', { analytics_storage: 'granted' });
  }
  sendCurrentPageView();
  const pending = pendingRouteEvent;
  pendingRouteEvent = null;
  if (pending && pending.key === route?.key) pending.send();
};

/** Called by App when auth state settles; the tag never loads before that. */
export const setAnalyticsSession = (session: { authResolved: boolean; isAdmin: boolean }) => {
  authResolved = session.authResolved;
  adminSession = session.isAdmin;
  if (!consentSubscribed) {
    consentSubscribed = true;
    subscribeAnalyticsConsent(sync);
  }
  sync();
};

/** One page view per distinct key (page plus entity identity); rerenders and canonical rewrites are ignored. */
export const trackPageView = (page: AppPage, key: string = page) => {
  route = { page, key };
  if (pendingRouteEvent?.key !== key) pendingRouteEvent = null;
  sync();
};

export const trackEvent = <N extends keyof AnalyticsEventParams>(
  name: N,
  params: AnalyticsEventParams[N],
) => {
  if (!isEligible() || !tagInitialized) return;
  const payload: Record<string, unknown> = { page_type: route?.page, surface: surface() };
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') payload[key] = value;
  });
  gtag('event', name, payload);
};

/** trackEvent for the route shown under `routeKey`: waits for consent/auth to resolve, dropped once the user navigates away. */
export const trackRouteEvent = <N extends keyof AnalyticsEventParams>(
  routeKey: string,
  name: N,
  params: AnalyticsEventParams[N],
) => {
  if (routeKey !== route?.key) return;
  pendingRouteEvent = { key: routeKey, send: () => trackEvent(name, params) };
  sync();
};
